'use client'

import { useLocalStorage } from '@/hooks/useLocalStorage'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

interface SearchContextValue {
  isOpen: boolean
  category: string
  history: string[]
  inputValue: string
  isTyping: boolean
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  query: string
  onClose: () => void
  onOpen: () => void
  onToggle: () => void
  addToRecents: (value: string) => void
  onClearRecents: () => void
  removeFromRecents: (value: string) => void
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setCategory: React.Dispatch<React.SetStateAction<string>>
  setHistory: React.Dispatch<React.SetStateAction<string[]>>
  setInputValue: React.Dispatch<React.SetStateAction<string>>
  setIsTyping: React.Dispatch<React.SetStateAction<boolean>>
  setQuery: React.Dispatch<React.SetStateAction<string>>
}

export const SearchContext = createContext<SearchContextValue>(
  {} as SearchContextValue
)

export default function SearchProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [category, setCategory] = useState('casino')
  const [history, setHistory] = useLocalStorage<string[]>('searchHistory', [
    'dice',
    'keno',
    'limbo',
    'plinko',
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [query, setQuery] = useState('')

  const onOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const onToggle = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [setIsOpen])

  const addToRecents = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()

      if (trimmedValue.length < 3) return

      setHistory((prev) => {
        const newValue = prev.filter((item) => !trimmedValue.startsWith(item))
        newValue.unshift(trimmedValue)
        return newValue.slice(0, 5)
      })
    },
    [setHistory]
  )

  const removeFromRecents = useCallback(
    (value: string) => {
      setHistory((prev) => prev.filter((tag) => tag !== value))

      if (inputRef.current) {
        inputRef.current.focus()
      }
    },
    [setHistory]
  )

  const onClearRecents = useCallback(() => {
    setHistory([])
  }, [setHistory])

  const contextValue = useMemo<SearchContextValue>(() => {
    return {
      category,
      history,
      inputRef,
      inputValue,
      isOpen,
      isTyping,
      query,
      addToRecents,
      onClearRecents,
      onClose,
      onOpen,
      onToggle,
      removeFromRecents,
      setCategory,
      setHistory,
      setInputValue,
      setIsOpen,
      setIsTyping,
      setQuery,
    }
  }, [
    category,
    history,
    inputValue,
    isOpen,
    isTyping,
    query,
    addToRecents,
    onClearRecents,
    onClose,
    onOpen,
    onToggle,
    removeFromRecents,
    setHistory,
  ])

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  )
}

export const useSearch = () => {
  const context = useContext(SearchContext)
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchContext')
  }
  return context
}
